<script lang="ts" setup>
import { Icon } from '@skillstown/leap';
import { useI18n } from 'vue-i18n';
import { useServiceAlerts } from '~~/stores/serviceAlerts';
import { useUser } from '~~/stores/user';
import { DateHelper } from '~~/helpers/DateHelper';
import { HTMLHelper } from '~~/helpers/HTMLHelper';

const { t } = useI18n();
const user = useUser();
const route = useRoute();
const serviceAlerts = useServiceAlerts();

const notifications = ref();
const showPanel = ref(false);

onMounted(() => {
  refresh();
  document.addEventListener('click', onFocusOut);
  document.addEventListener('visibilitychange', onVisibilityChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onFocusOut);
  document.removeEventListener('visibilitychange', onVisibilityChange);
});

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    refresh();
  }
}

watch(() => route.path, () => refresh());

function refresh(): void {
  if (!user.id) {
    return;
  }
  serviceAlerts.fetchIfStale();
}

function onClickBell(): void {
  showPanel.value = !showPanel.value;
}

function onFocusOut(event: MouseEvent): void {
  if (!showPanel.value) {
    return;
  }

  if (!HTMLHelper.isDescendant(notifications.value, event.target as HTMLElement)) {
    showPanel.value = false;
  }
}

function formatDate(date?: string): string {
  return date ? new DateHelper(date).toAppFormat() : '';
}
</script>

<template>
  <div class="notifications" ref="notifications">
    <button type="button" class="notifications__bell" :title="t('service_alerts.notifications')"
      :aria-label="t('service_alerts.notifications')" @click="onClickBell">
      <Icon icon="bell" variant="ghost" color="black" size="l" />
      <span v-if="serviceAlerts.hasAlerts" class="notifications__count">
        {{ serviceAlerts.alertCount }}
      </span>
    </button>

    <transition name="submenu-transition">
      <div v-show="showPanel" class="notifications__dropdown">
        <div class="menu">
          <span class="menu__title">{{ t('service_alerts.notifications') }}</span>

          <p v-if="!serviceAlerts.hasAlerts" class="menu__empty">
            {{ t('service_alerts.no_notifications') }}
          </p>

          <div v-for="alert in serviceAlerts.alerts" :key="`service-alert-${alert.id}`" class="alert">
            <div class="alert__header">
              <span class="alert__title">{{ alert.title }}</span>
              <span v-if="alert.createdDate" class="alert__date">{{ formatDate(alert.createdDate) }}</span>
            </div>
            <p class="alert__message">{{ alert.message }}</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="postcss" scoped>
.notifications {
  @apply relative flex items-center;
  margin-right: var(--size-4);
}

.notifications__bell {
  @apply relative flex cursor-pointer items-center;
}

.notifications__count {
  @apply absolute -right-2 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none;
  background-color: var(--color-orange, #f59e0b);
  color: var(--color-white);
}

.notifications__dropdown {
  @apply absolute right-0 top-full z-30 mt-3 w-screen max-w-[320px] transform overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5;
}

.menu {
  @apply relative grid gap-3 bg-white p-6;

  .menu__title {
    @apply text-sm font-semibold;
  }

  .menu__empty {
    @apply text-xs;
    color: var(--color-grey-pure);
  }
}

.alert {
  @apply border-t border-gray-200 pt-3;

  .alert__header {
    @apply flex items-baseline justify-between gap-3;
  }

  .alert__title {
    @apply text-sm font-semibold;
  }

  .alert__date {
    @apply whitespace-nowrap text-[10px] text-gray-500;
  }

  .alert__message {
    @apply mt-1 whitespace-pre-line text-xs;
    color: var(--color-grey-pure);
  }
}
</style>
